import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getProject, getProjectWarnings, trackUserEvent } from '../../services/api';
import { logger } from '../../utils/logger';
import ChatDrawer from '../ChatDrawer/ChatDrawer';
import { useConversation } from '../../hooks/useConversation';
import { pathWithPreservedParams } from '../../routing/navigation.js';
import { readAgentOpen, writeAgentOpen } from '../../utils/workspaceState';
import ProjectDetailContent from './ProjectDetailContent';
import './ProjectsView.css';
import './ProjectDetailModal.css';

/**
 * Project details page with the same chat drawer as Overview and Projects.
 */
const ProjectDetailView = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const { activeCompany, user } = useAuth();
  const userId = user?.id;
  const conversation = useConversation();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const [isChatDrawerOpen, setIsChatDrawerOpen] = useState(() => readAgentOpen(userId));
  const [agentHydratedFor, setAgentHydratedFor] = useState(userId ?? null);
  const lastTrackedProjectIdRef = useRef(null);
  const shownProjectKeyRef = useRef(null);
  const selectionKey = `${activeCompany?.id ?? ''}:${projectId ?? ''}`;
  const [selection, setSelection] = useState({ key: '', ids: null });
  const selectedIds = selection.key === selectionKey ? selection.ids : null;

  useEffect(() => {
    if (!activeCompany?.id || !projectId) return;

    const ac = new AbortController();
    const signal = ac.signal;
    const requestKey = `${activeCompany.id}:${projectId}`;
    const switchingProject = shownProjectKeyRef.current !== requestKey;

    const fetchProject = async () => {
      if (switchingProject) {
        setLoading(true);
        setNotFound(false);
        setProject(null);
      }

      try {
        logger.info(`Fetching project ${projectId} for company ${activeCompany.id}`);
        const response = await getProject(
          activeCompany.id,
          projectId,
          signal,
          switchingProject ? null : selectedIds
        );
        if (signal.aborted) return;
        if (!response) {
          if (switchingProject) setNotFound(true);
          return;
        }
        shownProjectKeyRef.current = requestKey;
        setProject(response);
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching project details:', err);
        if (switchingProject) setNotFound(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchProject();
    return () => ac.abort();
  }, [activeCompany?.id, projectId, selectedIds]);

  useEffect(() => {
    if (!activeCompany?.id || !projectId) {
      setWarnings(null);
      return;
    }

    const ac = new AbortController();
    const signal = ac.signal;

    const fetchWarnings = async () => {
      setWarnings(null);
      try {
        const response = await getProjectWarnings(activeCompany.id, projectId, signal);
        if (signal.aborted) return;
        setWarnings(response ?? null);
      } catch (err) {
        if (signal.aborted || err.name === 'AbortError') return;
        logger.error('Error fetching project warnings:', err);
        setWarnings(null);
      }
    };

    fetchWarnings();
    return () => ac.abort();
  }, [activeCompany?.id, projectId]);

  useEffect(() => {
    if (!userId) return;
    setIsChatDrawerOpen(readAgentOpen(userId));
    setAgentHydratedFor(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId || agentHydratedFor !== userId) return;
    writeAgentOpen(userId, isChatDrawerOpen);
  }, [userId, agentHydratedFor, isChatDrawerOpen]);

  const projectsPath = pathWithPreservedParams('/projects', searchParams);

  const selectFamily = () => {
    if (selectedIds === null) return;
    setSelection({ key: selectionKey, ids: null });
  };

  const toggleMember = (memberId) => {
    if (selectedIds === null) {
      setSelection({ key: selectionKey, ids: [memberId] });
      return;
    }
    if (selectedIds.includes(memberId)) {
      const next = selectedIds.filter((id) => id !== memberId);
      setSelection({ key: selectionKey, ids: next.length ? next : null });
      return;
    }
    setSelection({ key: selectionKey, ids: [...selectedIds, memberId] });
  };

  useEffect(() => {
    lastTrackedProjectIdRef.current = null;
  }, [activeCompany?.id]);

  useEffect(() => {
    if (!activeCompany?.id || !project?.project_id) return;
    if (lastTrackedProjectIdRef.current === project.project_id) return;
    lastTrackedProjectIdRef.current = project.project_id;
    trackUserEvent({
      companyId: activeCompany.id,
      event: 'view_project_details',
      operation: 'read',
      path: `/projects/${project.project_id}`
    });
  }, [project?.project_id, activeCompany?.id]);

  const drawer = (
    <ChatDrawer
      isOpen={isChatDrawerOpen}
      onToggle={() => setIsChatDrawerOpen(!isChatDrawerOpen)}
      context="projects"
      conversation={conversation}
    />
  );

  if (loading) {
    return (
      <div className="projects-view-container">
        <div className="projects-view project-detail-view">
          <div className="projects-loading">
            <div className="loading-spinner"></div>
            <p>{t('projects.loading')}</p>
          </div>
        </div>
        {drawer}
      </div>
    );
  }

  if (notFound || !project) {
    return <Navigate to={projectsPath} replace />;
  }

  return (
    <div className="projects-view-container">
      <div className="projects-view project-detail-view">
        <div className="project-detail-header">
          <Link className="project-detail-back" to={projectsPath}>
            ← {t('projects.backToList')}
          </Link>
          <h1 className="projects-title">{project.project_name || '-'}</h1>
        </div>
        <div className="project-detail-body">
          <ProjectDetailContent
            project={project}
            warnings={warnings}
            selectedProjectIds={selectedIds}
            onSelectFamily={selectFamily}
            onToggleMember={toggleMember}
          />
        </div>
      </div>
      {drawer}
    </div>
  );
};

export default ProjectDetailView;
